import type { Space } from '@cocoonspace/sdk-js/types/space'
import { TwicImg } from '@twicpics/components/react'
import { Image } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import { forwardRef } from 'react'
import {
	SidebarLayoutContent,
	SidebarLayoutSidebar,
	SidebarLayoutWrapper,
} from '~/components/layouts/sidebar-layout'
import { Button } from '~/components/ui/button'
import { SpaceDialogCarousel } from '~/domains/spaces/components/organisms/space-dialog-carousel'
import { SpaceDetailsImageCarousel } from '~/domains/spaces/components/space-details/space-details-images-carousel'
import { dialogsEmitter } from '~/domains/spaces/emitters/dialog.emitter'
import { cn } from '~/lib/utils'

export const SpaceDetailsPhotos = forwardRef<HTMLDivElement, { space?: Space }>(
	({ space }, ref) => {
		const { t } = useTranslation()

		if (!space) return null

		return (
			<div className='relative'>
				<div className='-mx-6 overflow-hidden lg:hidden'>
					<SpaceDetailsImageCarousel space={space} />
				</div>

				<div ref={ref}>
					<SidebarLayoutWrapper className='gap-4 md:flex-col lg:flex-row'>
						<SidebarLayoutContent className='-mx-6 relative lg:mx-0'>
							<div
								className='relative mb-4 hidden cursor-pointer overflow-hidden rounded-lg bg-slate-200/30 lg:block'
								onClick={() => dialogsEmitter.emit('spaceCarousel.open')}
							>
								<TwicImg
									src={space?.images[0]?.path!}
									alt={space?.title}
									ratio='16/9'
									focus='50px100p'
									preTransform='quality=80'
									step={1}
								/>

								<Button
									className='absolute bottom-4 left-4 bg-white/60 text-foreground hover:text-white'
									variant='default'
									size='sm'
								>
									<Image
										size={16}
										className='mr-2'
										strokeWidth={1.5}
									/>

									{t('actions:seePhotos.btnLabel')}
								</Button>
							</div>
						</SidebarLayoutContent>

						<SidebarLayoutSidebar className='mb-4 hidden flex-col gap-4 lg:flex'>
							{space?.images?.slice(1, 3).map((image, i) => (
								<div
									key={image.id}
									className={cn(
										'flex-1 cursor-pointer overflow-hidden rounded-lg',
										{ 'mb-[2px]': i === 0 },
									)}
									onClick={() =>
										dialogsEmitter.emit('spaceCarousel.open', { index: i + 1 })
									}
								>
									<TwicImg
										src={image.path}
										alt={space?.title}
										ratio='370/206'
										focus='50px100p'
									/>
								</div>
							))}
						</SidebarLayoutSidebar>
					</SidebarLayoutWrapper>
				</div>

				<SpaceDialogCarousel space={space!} />
			</div>
		)
	},
)

SpaceDetailsPhotos.displayName = 'SpaceDetailsPhotos'
